import { Tabs as ArkTabs } from "@ark-ui/react";
import type { ReactElement, ReactNode } from "react";
import { cloneElement } from "react";
import { twJoin } from "tailwind-merge";

import { isDefined } from "@/helpers/util";

import { FullSizeLoader } from "./FullSizeLoader";
import { NumericBadge } from "./NumericBadge";

type TabIcon = ReactElement<{
  size?: number | string;
  tone?: "info";
}>;

export type TabItem<TTab extends string> = {
  label: string;
  value: TTab;
  "data-testid"?: string;
  icon?: TabIcon;
  iconTone?: "default" | "info";
  isHidden?: boolean;
  isDisabled?: boolean;
  count?: number;
  showZeroCount?: boolean;
};

type TabsRootProps<TTab extends string> = {
  activeTab: TTab;
  tabs: TabItem<TTab>[];
  children: ReactNode;
  variant?: "inline" | "contained";
  onSelectTab: (tab: TTab) => void;
};

function TabsRoot<TTab extends string>({
  activeTab,
  tabs,
  children,
  variant = "inline",
  onSelectTab,
}: TabsRootProps<TTab>): ReactNode {
  const visibleTabs = tabs.filter((tab) => !tab.isHidden);

  return (
    <ArkTabs.Root
      value={activeTab}
      onValueChange={(event) => onSelectTab(event.value as TTab)}
      className={twJoin(
        "flex flex-1 flex-col gap-tabs-root pb-tabs-bottom",
        variant === "contained" && "grow-0 rounded-lg bg-background-default p-space-md",
      )}
      unmountOnExit
      lazyMount
    >
      {visibleTabs.length > 1 && (
        <ArkTabs.List className="relative flex w-full no-scrollbar items-center justify-start overflow-x-auto">
          {tabs.map((tab) => {
            if (tab.isHidden) {
              return null;
            }

            const isSelected = tab.value === activeTab;

            return (
              <ArkTabs.Trigger
                key={tab.value}
                value={tab.value}
                disabled={tab.isDisabled}
                data-testid={tab["data-testid"]}
                className={twJoin(
                  "group relative flex items-center gap-space-xs px-space-md py-space-sm",
                  isSelected ? "text-text-brand-secondary" : "text-text-primary",
                )}
              >
                {tab.icon &&
                  cloneElement(tab.icon, {
                    size: 16,
                    tone: tab.iconTone === "info" ? "info" : undefined,
                  })}
                <span className="text-body whitespace-nowrap group-disabled:text-text-disabled group-data-selected:text-body-emphasis">
                  {tab.label}
                </span>
                {isDefined(tab.count) && (tab.count > 0 || tab.showZeroCount) && <NumericBadge count={tab.count} />}
                {isSelected && (
                  <span className="absolute bottom-0 left-0 z-raised h-tabs-indicator w-full rounded-t-full bg-background-brand-secondary" />
                )}
              </ArkTabs.Trigger>
            );
          })}
          <span className="absolute bottom-0 left-0 z-content w-full border-b border-border-default" />
        </ArkTabs.List>
      )}

      <div className="flex flex-1">{children}</div>
    </ArkTabs.Root>
  );
}

type TabsContentProps<TTab extends string> = {
  value: TTab;
  children: ReactNode;
  isLoading?: boolean;
};

function TabsContent<TTab extends string>({ value, children, isLoading }: TabsContentProps<TTab>): ReactNode {
  return (
    <ArkTabs.Content value={value} key={value} className="flex w-full flex-1 flex-col">
      {isLoading ? (
        <div className="flex flex-1">
          <FullSizeLoader withPadding noDelay />
        </div>
      ) : (
        <div className="flex flex-1 flex-col">{children}</div>
      )}
    </ArkTabs.Content>
  );
}

export const Tabs = {
  Root: TabsRoot,
  Content: TabsContent,
};
